import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, MessageCircle, ArrowUpRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal, MaskLine } from "@/components/Reveal";
import { GoldButton, OutlineButton, SectionLabel, waLink } from "@/components/ui-kit";
import { FinalCTA, Marquee } from "@/components/blocks";
import { images, heroClaims, strengths, services, courses } from "@/data/site";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative h-screen min-h-[680px] flex items-center grain overflow-hidden" data-testid="home-hero">
      <motion.img style={{ y, scale }} src={images.hero} alt="Body Evolution 2.0 palestra a Lusciano" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/40" />
      <div className="relative z-10 max-w-[1400px] mx-auto w-full px-5 lg:px-10">
        <Reveal><SectionLabel dark>Palestra a Lusciano · Aperti H24 da settembre</SectionLabel></Reveal>
        <h1 className="mt-6 text-ivory font-display font-medium tracking-tighter text-5xl sm:text-7xl lg:text-8xl leading-[0.95]">
          <MaskLine delay={0.1}>Body Evolution</MaskLine>
          <MaskLine delay={0.22}><span className="text-gold">2.0</span></MaskLine>
        </h1>
        <Reveal delay={0.45}>
          <p className="mt-7 text-ivory/80 text-lg lg:text-2xl font-light max-w-2xl">
            Il tuo percorso fitness a Lusciano, ogni giorno, a ogni ora.
          </p>
        </Reveal>
        <Reveal delay={0.55}>
          <p className="mt-4 text-ivory/60 max-w-xl leading-relaxed">
            Una palestra moderna, accogliente e completa: sala pesi, corsi fitness, area olistica,
            personal training e professionisti dedicati al tuo benessere.
          </p>
        </Reveal>
        <Reveal delay={0.65}>
          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <GoldButton to="/contatti" data-testid="hero-cta-trial"><CalendarCheck size={18} /> Prenota una prova gratuita</GoldButton>
            <OutlineButton href={waLink()} target="_blank" rel="noreferrer" data-testid="hero-cta-whatsapp"><MessageCircle size={18} /> Scrivici su WhatsApp</OutlineButton>
          </div>
        </Reveal>
        <Reveal delay={0.8}>
          <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3">
            {heroClaims.map((c) => (
              <span key={c} className="text-xs tracking-[0.15em] uppercase text-ivory/70 flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-gold" /> {c}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Seo title="Body Evolution 2.0 | Palestra a Lusciano" description="Body Evolution 2.0 è la palestra a Lusciano con sala pesi, corsi fitness, sala olistica, personal training e professionisti per percorsi su misura." />
      <Hero />

      {/* Manifesto */}
      <section className="bg-ivory py-24 lg:py-36" data-testid="home-manifesto">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Reveal><SectionLabel>La nostra visione</SectionLabel></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display font-medium tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight text-ink-800">
                Non una semplice palestra. Un percorso costruito su di te.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <Reveal delay={0.2}>
              <p className="text-ink-800/70 text-lg leading-relaxed">
                Body Evolution 2.0 nasce per offrire un'esperienza di allenamento completa, dove ogni persona
                viene ascoltata, guidata e seguita in base ai propri obiettivi. Dalla sala pesi ai corsi,
                dal personal training ai professionisti del benessere, ogni servizio è pensato per accompagnarti
                con metodo, attenzione e continuità.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee items={["Sala Pesi", "Personal Training", "Corsi Fitness", "Sala Olistica", "Kids & Family", "Professionisti"]} />

      {/* Strengths */}
      <section className="bg-ink text-ivory grain py-24 lg:py-32 relative" data-testid="home-strengths">
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 lg:px-10">
          <Reveal><SectionLabel dark>Perché scegliere Body Evolution 2.0</SectionLabel></Reveal>
          <Reveal delay={0.1}><h2 className="mt-6 font-display font-medium tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-2xl">I nostri punti di forza</h2></Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {strengths.map((s, i) => (
              <Reveal key={s.n} delay={(i % 3) * 0.08}>
                <div className="lift bg-ink p-8 lg:p-10 h-full hover:bg-surface">
                  <span className="text-stroke font-display text-5xl font-extrabold">{s.n}</span>
                  <h3 className="mt-6 font-display text-xl font-medium text-gold">{s.title}</h3>
                  <p className="mt-3 text-ivory/60 text-sm leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="bg-ivory py-24 lg:py-32" data-testid="home-services">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <Reveal><SectionLabel>I servizi</SectionLabel></Reveal>
          <Reveal delay={0.1}><h2 className="mt-6 font-display font-medium tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-2xl text-ink-800">Tutto ciò di cui hai bisogno, in un unico spazio</h2></Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.08}>
                <Link to={s.to} data-testid={`service-card-${i}`} className="lift group block relative h-80 rounded-sm overflow-hidden border border-black/10">
                  <img src={images[s.img]} alt={s.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
                  <div className="absolute inset-0 p-7 flex flex-col justify-end text-ivory">
                    <div className="flex items-center justify-between">
                      <h3 className="font-display text-2xl font-medium">{s.title}</h3>
                      <ArrowUpRight className="text-gold" size={22} />
                    </div>
                    <p className="mt-2 text-ivory/70 text-sm">{s.text}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Courses spotlight */}
      <section className="bg-sand py-24 lg:py-32" data-testid="home-courses">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Reveal><SectionLabel>Corsi in evidenza</SectionLabel></Reveal>
              <Reveal delay={0.1}><h2 className="mt-6 font-display font-medium tracking-tight text-3xl sm:text-4xl lg:text-5xl text-ink-800">Allenati con energia e metodo</h2></Reveal>
            </div>
            <Reveal delay={0.2}><OutlineButton to="/corsi-fitness" light={false}>Vedi tutti i corsi</OutlineButton></Reveal>
          </div>
          <div className="mt-12 flex flex-wrap gap-3">
            {courses.slice(0, 12).map((c, i) => (
              <Reveal key={c.slug} delay={(i % 6) * 0.04}>
                <Link to={`/corsi/${c.slug}`} className="lift px-6 py-3 rounded-full bg-white border border-black/10 hover:border-gold hover:text-gold font-display text-lg text-ink-800">
                  {c.name}
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
